import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getLLMRouter } from './llm/llm-gate.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { nlToLatexModel, nlToLatexTemperature } from '../../shared/models.js';
import { loadAndAssert, requireOwnerIdentity } from '../authz/access.js';
import { Exam } from '../exam.js';
import { addCostToExam } from './add-cost.js';

export const translateNaturalLanguageToLatexMutation = registerMutation({
  operationName: 'translateNaturalLanguageToLatexMutation',
  type: () => String,
  params: [
    { name: 'text', type: () => String },
    { name: 'examId', type: () => String },
  ],
  resolve: (text: string, examId?: string) => async () => {
    // Paid LLM call: needs a session identity, and the cost is booked onto the exam — only the caller's own.
    await requireOwnerIdentity();
    if (examId) await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const router = getLLMRouter({}, 'interactive');

    const llmCall = router.chat.completions.create({
      model: nlToLatexModel,
      provider: 'openrouter',
      temperature: nlToLatexTemperature,
      messages: [
        {
          role: 'system',
          content: `Du übersetzt eine natürlichsprachliche Beschreibung einer mathematischen Formel in LaTeX.
Antworte NUR mit dem reinen LaTeX-Code der Formel (ohne $, $$ oder \\[ \\]-Umschließung, ohne Erklärung, ohne Markdown-Codeblock).
Beispiel: "Integral von 0 bis 10 von x hoch 2" -> \\int_{0}^{10} x^2 \\, dx`,
        },
        { role: 'user', content: text },
      ],
    });

    llmCall.catch(err => {
      logger.warn({ err }, 'Background llmCall rejected after timeout race concluded.');
    });

    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new ExpectedError('translateNaturalLanguageToLatex timeout'));
      }, 8_000);
    });

    try {
      const response = await Promise.race([llmCall, timeout]);
      await addCostToExam(response, examId, 'nlToLatex');
      const raw = response.choices[0]?.message.content ?? '';
      return raw
        .trim()
        .replace(/^```[a-z]*\n?|```$/g, '')
        .trim();
    } catch (error) {
      logger.warn({ err: error }, 'translateNaturalLanguageToLatexMutation failed or timed out.');
      throw new ExpectedError('Die Übersetzung ist fehlgeschlagen. Bitte versuche es erneut.');
    }
  },
});
